import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { DURATION, EASE_IN, EASE_OUT, SPRING_SNAPPY } from '../tokens/motion'
import { Icons } from '../icons/registry'
import { cn } from '../lib/cn'

export type ToastTone = 'info' | 'success' | 'danger'

export interface ToastInput {
  title: string
  body?: string
  tone?: ToastTone
}

interface ToastItem {
  id: number
  title: string
  body?: string
  tone: ToastTone
}

let nextId = 1
let items: ToastItem[] = []
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

/**
 * An announcement from anywhere: `notify({ title: 'Saved.' })` — no hook, no
 * context, no provider. It rises from the bottom edge, waits long enough to
 * be read, and leaves by itself; pointing at it holds it in place. Three at
 * a time — a fourth pushes the oldest out, because a stack that grows without
 * end is a log, not a message.
 */
export function notify(input: ToastInput) {
  const item: ToastItem = { tone: 'info', ...input, id: nextId++ }
  items = [...items.slice(-2), item]
  emit()
}

export function dismissToast(id: number) {
  items = items.filter((item) => item.id !== id)
  emit()
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => items,
  )
}

const TONE_ICON: Record<ToastTone, ReactNode> = {
  info: <Icons.bell />,
  success: <Icons.check />,
  danger: <Icons.error />,
}

const TONE_MARK: Record<ToastTone, string> = {
  info: 'text-muted-foreground',
  success: 'text-foreground',
  danger: 'text-danger',
}

/** How long each tone earns before it leaves by itself. A refusal stays a beat longer. */
const TONE_DURATION: Record<ToastTone, number> = {
  info: 4500,
  success: 4500,
  danger: 6500,
}

export function Toasts() {
  const toasts = useToasts()
  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-70 flex flex-col items-center gap-2 px-4 sm:bottom-6"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {toasts.map((item) => (
          <ToastRow key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  )
}

/**
 * One message. Its clock stops while the pointer is over it — a person
 * reading is a person not yet done — and resumes with what remains.
 */
function ToastRow({ item }: { item: ToastItem }) {
  const timer = useRef<number | undefined>(undefined)
  const deadline = useRef(0)
  const remaining = useRef(TONE_DURATION[item.tone])

  useEffect(() => {
    deadline.current = Date.now() + remaining.current
    timer.current = window.setTimeout(() => dismissToast(item.id), remaining.current)
    return () => window.clearTimeout(timer.current)
  }, [item.id])

  const hold = () => {
    remaining.current = Math.max(deadline.current - Date.now(), 800)
    window.clearTimeout(timer.current)
  }
  const resume = () => {
    deadline.current = Date.now() + remaining.current
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => dismissToast(item.id), remaining.current)
  }

  return (
    <motion.div
      layout
      transition={SPRING_SNAPPY}
      role={item.tone === 'danger' ? 'alert' : 'status'}
      onPointerEnter={hold}
      onPointerLeave={resume}
      className="pointer-events-auto flex w-full max-w-[26rem] items-start gap-3 rounded-2xl border bg-surface px-4 py-3 shadow-modal"
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: DURATION.slow, ease: EASE_OUT } }}
      exit={{ opacity: 0, y: 8, scale: 0.98, transition: { duration: DURATION.fast, ease: EASE_IN } }}
    >
      <span className={cn('mt-0.5 shrink-0', TONE_MARK[item.tone])} aria-hidden>
        {TONE_ICON[item.tone]}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-ui font-medium text-foreground">{item.title}</span>
        {item.body ? <span className="mt-0.5 block break-words text-caption text-muted-foreground">{item.body}</span> : null}
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismissToast(item.id)}
        className="-mr-1 -mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
      >
        <Icons.close />
      </button>
    </motion.div>
  )
}
